import { useEffect, useRef } from 'react';
import type { Nullable } from 'corgi-types';
import { throttle } from 'lodash-es';

import { breakpoints as themeBreakpoints } from '../theme/theme';
import { isClient } from '../utils/isClient';
import { getInteger } from '../utils/number';

const breakpoints = themeBreakpoints.map(getInteger);

const THROTTLE_WAIT = 250;

const getBreakpoint = (width: number) => {
  const [tablet, desktop, largeDesktop] = breakpoints;

  if (width < tablet) {
    return 0;
  } else if (width >= tablet && width < desktop) {
    return tablet;
  } else if (width >= desktop && width < largeDesktop) {
    return desktop;
  }

  return largeDesktop;
};

const isBreakpointChange = (currentWidth: Nullable<number>, nextWidth: number) => {
  const currentBreakpoint = getBreakpoint(currentWidth);
  const nextBreakpoint = getBreakpoint(nextWidth);

  return currentBreakpoint !== nextBreakpoint;
};

export const useResizeOnBreakpoint = (handler: () => void) => {
  const width = useRef<Nullable<number>>(isClient() ? window.innerWidth : null);

  const handleResize = throttle(() => {
    if (isBreakpointChange(width.current, window.innerWidth)) {
      width.current = window.innerWidth;
      handler();
    }
  }, THROTTLE_WAIT);

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [handler]);
};

export const useResize = (handler: () => void) => {
  const handleResize = throttle(() => {
    handler();
  }, THROTTLE_WAIT);

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [handler]);
};
